<template>
	<div>
		<!-- Stocks container skeleton -->
		<div
			class="stocks-container gap-4"
			:class="{ 'md:grid grid-cols-2 xl:block': !isQuotePage }"
		>
			<div
				v-for="n in visibleItemCount"
				:key="'skeleton-' + n"
				:class="[
					'flex justify-between items-center w-full max-w-md bg-transparent border-b py-3',
					alternative ? 'border-primary-200/[0.2]' : 'border-slate-200'
				]"
			>
				<!-- Left side: Symbol and name -->
				<div :class="[size === 'sm' ? 'w-[68px]' : 'w-[140px]']">
					<div
						class="h-4 w-12 rounded mb-1"
						:class="[
							alternative ? 'bg-white/[.1]' : 'bg-slate-300/[0.1]',
							staticMode ? '' : 'animate-pulse'
						]"
					></div>
					<div
						class="h-3 w-20 rounded"
						:class="[
							alternative ? 'bg-white/[.1]' : 'bg-slate-300/[0.1]',
							staticMode ? '' : 'animate-pulse'
						]"
					></div>
				</div>

				<!-- Chart skeleton -->
				<div
					:class="[
						'flex items-center',
						size === 'sm' ? 'max-w-[68px] h-[40px]' : 'ml-auto'
					]"
				>
					<div
						class="w-full h-[40px] rounded"
						:class="[
							alternative ? 'bg-white/[.1]' : 'bg-slate-300/[0.1]',
							staticMode ? '' : 'animate-pulse'
						]"
					></div>
				</div>

				<!-- Right side: Price and change -->
				<div :class="['text-right ml-3', size === 'sm' ? '' : 'min-w-[112px]']">
					<div
						class="h-4 w-16 rounded mb-1 ml-auto"
						:class="[
							alternative ? 'bg-white/[.1]' : 'bg-slate-300/[0.1]',
							staticMode ? '' : 'animate-pulse'
						]"
					></div>
					<div
						class="h-3 w-20 rounded ml-auto"
						:class="[
							alternative ? 'bg-white/[.1]' : 'bg-slate-300/[0.1]',
							staticMode ? '' : 'animate-pulse'
						]"
					></div>
				</div>
			</div>
		</div>

		<!-- Show More button skeleton -->
		<div
			v-if="itemCount > 3 && !alternative"
			class="z-10 md:hidden xl:block py-4"
		>
			<div
				class="h-8 w-24 rounded mx-auto"
				:class="[
					alternative ? 'bg-white/[.1]' : 'bg-slate-300/[0.1]',
					staticMode ? '' : 'animate-pulse'
				]"
			></div>
		</div>
	</div>
</template>

<script setup lang="ts">
interface Props {
	itemCount?: number;
	alternative?: boolean;
	isQuotePage?: boolean;
	size?: string;
	showAll?: boolean;
	staticMode?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	itemCount: 10,
	alternative: false,
	isQuotePage: false,
	size: "sm",
	showAll: false,
	staticMode: false
});

// Calculate visible items count
const visibleItemCount = computed(() => {
	return props.showAll ? props.itemCount : Math.min(7, props.itemCount);
});

// Calculate container height for skeleton
const containerHeight = computed(() => {
	// Approximate height calculation for skeleton
	const titleHeight = 60; // py-4 + border + text
	const itemHeight = 60; // py-3 + content
	const buttonHeight = 60; // py-4 + button

	const visibleItems = props.showAll
		? props.itemCount
		: Math.min(3, props.itemCount);
	const itemsHeight = itemHeight * visibleItems;

	return titleHeight + itemsHeight + buttonHeight;
});
</script>

<style scoped>
/* Custom pulse animation for better skeleton effect */
@keyframes pulse {
	0%,
	100% {
		opacity: 0.4;
	}
	50% {
		opacity: 0.8;
	}
}

.animate-pulse {
	animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>
